import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import { Link } from '@tanstack/react-router';

import { marketing } from '../../styles/theme';
import { usePostHog } from '../widgets/PostHogProvider';

interface CTAButton {
    label: string;
    href: string;
    external?: boolean;
}

export interface CallToActionCardProps {
    title: string;
    description?: string;
    primaryCTA: CTAButton;
    secondaryCTA?: CTAButton;
    /** Where this card sits (e.g. 'home_bottom_cta') — identifies the CTA in
     * the `cta_clicked` funnel event. Instrument-only: never changes routing. */
    location?: string;
}

export function CallToActionCard({
    title,
    description,
    primaryCTA,
    secondaryCTA,
    location,
}: CallToActionCardProps) {
    const { capture } = usePostHog();

    const trackCta = (cta: CTAButton, ctaType: 'primary' | 'secondary') =>
        capture('cta_clicked', {
            location,
            cta_title: title,
            cta_label: cta.label,
            cta_href: cta.href,
            cta_type: ctaType,
            cta_external: cta.external ?? false,
        });

    return (
        <Paper
            elevation={10}
            sx={{
                background: marketing.gradients.ctaCard,
                borderRadius: 4,
                px: { xs: 4, md: 8 },
                py: { xs: 6, md: 8 },
                textAlign: 'center',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                    variant="h2"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 700, mb: 3 }}
                >
                    {title}
                </Typography>

                {description && (
                    <Typography
                        variant="h6"
                        sx={{ mb: 4, maxWidth: 600, mx: 'auto', opacity: 0.9, fontWeight: 400 }}
                    >
                        {description}
                    </Typography>
                )}

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    justifyContent="center"
                    alignItems="center"
                >
                    {/* Primary Button */}
                    <Button
                        variant="contained"
                        size="large"
                        component={primaryCTA.external ? 'a' : Link}
                        {...(primaryCTA.external ? { href: primaryCTA.href } : { to: primaryCTA.href })}
                        onClick={() => trackCta(primaryCTA, 'primary')}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                            '&:hover': {
                                bgcolor: 'primary.light',
                                boxShadow: '0 6px 20px rgba(77, 101, 255, 0.25)',
                            },
                        }}
                    >
                        {primaryCTA.label}
                    </Button>

                    {/* Secondary Button */}
                    {secondaryCTA && (
                        <Button
                            variant="outlined"
                            size="large"
                            component={secondaryCTA.external ? 'a' : Link}
                            {...(secondaryCTA.external ? { href: secondaryCTA.href } : { to: secondaryCTA.href })}
                            onClick={() => trackCta(secondaryCTA, 'secondary')}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                fontSize: '1.1rem',
                                fontWeight: 500,
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            {secondaryCTA.label}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}
